import React from 'react';
import BBBSeal from '@/components/BBBSeal';

/**
 * TrustBadges
 * ───────────
 * Compact horizontal row of trust signals: BBB Accredited + Proudly Canadian.
 * Drop wherever conversion matters (forms, CTAs, pricing).
 *
 * Variants:
 *   variant="row"       — horizontal, balanced spacing (default; forms, CTAs).
 *   variant="inline"    — small, side-by-side; good above an H1 hero.
 *   variant="centered"  — centered row with a short tagline above.
 *
 *   size="sm" → small (80x101 BBB, h-12 Canadian)
 *   size="md" → standard (80x144 BBB, h-16 Canadian)  [default]
 *   size="lg" → large    (80x144 BBB, h-20 Canadian)
 */
const CANADIAN_SIZE = { sm: 'h-12 w-12', md: 'h-16 w-16', lg: 'h-20 w-20' };
const BBB_SIZE = { sm: 'bbb-seal-sm', md: '', lg: '' };

const BadgeBBB = ({ size }) => <BBBSeal className={BBB_SIZE[size] || ''} />;

const BadgeCanadian = ({ size }) => (
  <img
    src="/proudly-canadian.png"
    alt="Proudly Canadian"
    aria-label="Proudly Canadian"
    data-testid="trust-badge-canadian"
    className={`${CANADIAN_SIZE[size] || CANADIAN_SIZE.md} rounded-full object-cover`}
    loading="lazy"
  />
);

const TrustBadges = ({
  variant = 'row',
  size = 'md',
  showTagline = false,
  className = '',
  tagline = 'Trusted by businesses across BC',
}) => {
  const badges = (
    <div className="flex items-center gap-5 flex-wrap">
      <BadgeBBB size={size} />
      <BadgeCanadian size={size} />
    </div>
  );

  if (variant === 'inline') {
    return (
      <div data-testid="trust-badges" className={`flex items-center gap-4 ${className}`}>
        <BadgeBBB size={size} />
        <BadgeCanadian size={size} />
      </div>
    );
  }

  if (variant === 'centered') {
    return (
      <div data-testid="trust-badges" className={`flex flex-col items-center gap-3 ${className}`}>
        {showTagline && (
          <span className="text-xs tracking-[0.15em] uppercase text-gray-500 font-medium">
            {tagline}
          </span>
        )}
        {badges}
      </div>
    );
  }

  // Default: "row"
  return (
    <div data-testid="trust-badges" className={`flex items-center gap-5 ${className}`}>
      {showTagline && (
        <span className="text-xs text-gray-500 uppercase tracking-wider whitespace-nowrap hidden sm:inline">
          {tagline}
        </span>
      )}
      {badges}
    </div>
  );
};

export default TrustBadges;
